// Handles search input, dropdown display, and AJAX fetching of results
//
// @see SearchController#showings for backend endpoint
// @see app/views/search/_showing_results.html.haml for result partial

import FetchUtils from '../utils/fetch_utils';
import SearchResultActions from './search_result_actions';
import ShowingsViewPopup from '../application/dashboard/showings/view_popup';

class TopbarSearch {
  #resultActions;
  #showingsViewPopup;

  static DEBOUNCE_MS = 300;
  static MIN_QUERY_LENGTH = 3;

  // Returns the form field name based on current search type
  get searchParamName() {
    return this.currentType === 'prospect' ? 'people_search' : 'address';
  }

  constructor(container) {
    this.container = container;
    this.input = container.querySelector('.js-topbar-search-input');
    this.dropdown = container.querySelector('.js-topbar-search-dropdown');
    this.resultsContainer = container.querySelector('.js-topbar-search-results');
    this.clearButton = container.querySelector('.js-topbar-search-clear');
    this.tabsContainer = container.querySelector('.js-topbar-search-tabs');
    this.typeToggle = container.querySelector('.js-topbar-search-type-toggle');
    this.typeTrigger = container.querySelector('.js-topbar-search-type-trigger');
    this.typeMenu = container.querySelector('.js-topbar-search-type-menu');
    this.typeLabel = container.querySelector('.js-topbar-search-type-label');

    // Get URLs from data attributes (Rails-generated)
    this.searchUrl = container.dataset.searchUrl;

    this.debounceTimer = null;
    this.currentFilter = '2'; // Default: FILTER_UPCOMING
    this.currentType = 'location';
    this.isOpen = false;
    this.isTypeMenuOpen = false;
    this.wasDropdownOpenBeforeModal = false;

    // Cache for current search results
    // Structure: { query: string, type: string, results: { [filterId]: { html: string, count: string } } }
    this.searchCache = null;

    // Track in-flight requests to cancel on new search
    this.abortController = null;
  }

  init() {
    this.#showingsViewPopup = new ShowingsViewPopup();
    this.#showingsViewPopup.init();
    this.initResultActions();
    this.bindEvents();
    this.bindModalCloseListener();
    this.setInputPlaceholder();
  }

  bindEvents() {
    // Input events
    this.input.addEventListener('input', this.handleInput.bind(this));
    this.input.addEventListener('focus', this.handleFocus.bind(this));

    // Clear button
    this.clearButton.addEventListener('click', this.handleClear.bind(this));

    // Filter tabs
    this.tabsContainer.addEventListener('click', this.handleTabClick.bind(this));

    // Type dropdown
    this.typeTrigger.addEventListener('click', this.handleTypeTriggerClick.bind(this));
    this.typeMenu.addEventListener('click', this.handleTypeOptionClick.bind(this));

    // Close on outside click
    document.addEventListener('click', this.handleOutsideClick.bind(this));

    // Close on escape
    document.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  // Result row click handling (edit/message/view-popup) lives in the shared
  // SearchResultActions module so the universal search (SMD-658) reuses the
  // exact same SMD-439 side-panel integration.
  initResultActions() {
    this.#resultActions = new SearchResultActions({
      onBeforeOpen: () => { this.wasDropdownOpenBeforeModal = this.isOpen; },
      onAfterOpen: () => this.closeDropdown()
    });
  }

  // Listen for modal/popup close events to restore dropdown state
  bindModalCloseListener() {
    // Handle modal close (edit showing)
    document.addEventListener('sm-modal:closed', () => {
      this.restoreDropdownIfNeeded();
    });

    // Handle two-way messaging popup close
    document.addEventListener('two-way-messaging:closed', () => {
      this.restoreDropdownIfNeeded();
    });

    document.addEventListener('sm-modal:opened', (e) => {
      const container = e.detail?.modalContainer;
      if (container?.querySelector('.js-showing-view-popup')) {
        this.closeDropdown();
      }
      this.#showingsViewPopup?.init('.js-sm-modal');
    });

    // Handle showing updated - refresh search results to show updated data
    document.addEventListener('showing:updated', () => {
      // Invalidate cache and refresh search
      this.searchCache = null;
      if (this.resultsContainer.innerHTML.trim()) {
        this.performSearchAllFilters(this.input.value.trim());
      }
    });
  }

  // Restore dropdown if it was open before a modal/popup was opened
  restoreDropdownIfNeeded() {
    if (this.wasDropdownOpenBeforeModal) {
      // Use setTimeout to avoid the close click triggering handleOutsideClick
      // which would immediately close the dropdown we're trying to open
      setTimeout(() => {
        this.openDropdown();
      }, 0);
      this.wasDropdownOpenBeforeModal = false;
    }
  }

  handleInput(e) {
    const query = e.target.value.trim();

    // Show/hide clear button
    this.toggleClearButton(query.length > 0);

    // Debounce search
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      // If query is empty, show default results
      if (query.length === 0) {
        this.performSearchAllFilters(query);
      // If query has content but less than min length, show hint
      } else if (query.length < TopbarSearch.MIN_QUERY_LENGTH) {
        this.showMessage(`Type at least ${TopbarSearch.MIN_QUERY_LENGTH} characters to search`);
        this.openDropdown();
      // Otherwise perform search
      } else {
        this.performSearchAllFilters(query);
      }
    }, TopbarSearch.DEBOUNCE_MS);
  }

  handleFocus() {
    // Always show dropdown on focus
    // Check if we have cached results for empty query, otherwise load
    if (this.searchCache && this.searchCache.query === '' && this.searchCache.type === this.currentType) {
      this.displayCachedResults();
      this.openDropdown();
    } else if (!this.resultsContainer.innerHTML.trim()) {
      this.performSearchAllFilters('');
    } else {
      this.openDropdown();
    }
  }

  handleClear() {
    this.input.value = '';
    this.toggleClearButton(false);
    // Show default results instead of closing dropdown
    this.performSearchAllFilters('');
    this.input.focus();
  }

  handleOutsideClick(e) {
    if (!this.container.contains(e.target)) {
      this.closeDropdown();
      this.closeTypeMenu();
    } else if (!this.typeToggle.contains(e.target)) {
      this.closeTypeMenu();
    }
  }

  handleKeydown(e) {
    if (e.key === 'Escape') {
      this.closeDropdown();
      this.closeTypeMenu();
      this.input.blur();
    } else if (e.key === 'Enter' && document.activeElement === this.input) {
      // Enter key in search input: navigate to dashboard with search params (if query exists)
      this.handleEnterKey(e);
    }
  }

  // Handle Enter key press in search input
  // If query exists, navigate to dashboard with search params (same as "More Showings" link)
  // If query is empty, do nothing
  handleEnterKey(e) {
    const query = this.input.value.trim();

    // Do nothing if query is empty
    if (!query) {
      return;
    }

    e.preventDefault();

    // Try to submit the form if it exists (it populates hidden fields and submits)
    const form = this.resultsContainer.querySelector('.js-topbar-all-results-form');
    if (form) {
      // Trigger form submit - the submit handler will populate hidden fields
      form.requestSubmit();
    } else {
      // Fallback: navigate directly to dashboard with search params
      this.buildAndSubmitSearchForm({ query, filter: this.currentFilter });
    }
  }

  // Submit form to navigate to dashboard with search params
  // Uses the hidden form from the DOM (which includes CSRF token from Rails)
  // @param {Object} options
  // @param {string} options.query - Search query (optional)
  // @param {string} options.filter - Filter value
  // @param {string} options.highlight - Showing ID to highlight (optional)
  buildAndSubmitSearchForm({ query = null, filter, highlight = null, city = null }) {
    const form = this.container.querySelector('.js-topbar-search-form');

    const peopleSearchField = form.querySelector('.js-form-people-search');
    const cityField = form.querySelector('.js-form-city');
    const wildcardAddressField = form.querySelector('.js-form-wildcard-address');
    const highlightField = form.querySelector('.js-form-highlight');

    // Clear and disable all optional fields first
    // Disabled fields are not submitted, matching original behavior
    peopleSearchField.value = '';
    peopleSearchField.disabled = true;
    wildcardAddressField.value = '';
    wildcardAddressField.disabled = true;
    highlightField.value = '';
    highlightField.disabled = true;
    cityField.value = '';
    cityField.disabled = true;

    // Enable and populate only the fields we need
    if (query) {
      const targetField = this.searchParamName === 'people_search' ? peopleSearchField : wildcardAddressField;
      targetField.value = query;
      targetField.disabled = false;
    }

    form.querySelector('.js-form-sf').value = filter;

    if (highlight) {
      highlightField.value = highlight;
      highlightField.disabled = false;
    }

    if (city && !wildcardAddressField.disabled) {
      cityField.value = city;
      cityField.disabled = false;
    }

    form.submit();
  }

  handleTabClick(e) {
    const tab = e.target.closest('[data-filter]');
    if (!tab) return;

    e.preventDefault(); // Prevent link navigation

    const filter = tab.dataset.filter;
    if (filter === this.currentFilter) return;

    // Update active tab styling (using .current class to match dashboard filter styles)
    this.tabsContainer.querySelectorAll('[data-filter]').forEach(t => {
      t.classList.remove('current');
    });
    tab.classList.add('current');

    // Update filter
    this.currentFilter = filter;

    // Display results from cache (no new request needed)
    this.displayCachedResults();
  }

  handleTypeTriggerClick(e) {
    e.stopPropagation();
    if (this.isTypeMenuOpen) {
      this.closeTypeMenu();
    } else {
      this.openTypeMenu();
    }
  }

  handleTypeOptionClick(e) {
    const option = e.target.closest('[data-search-type]');
    if (!option) return;

    const type = option.dataset.searchType;
    if (type === this.currentType) {
      this.closeTypeMenu();
      return;
    }

    // Update active option styling
    this.typeMenu.querySelectorAll('.js-topbar-search-type-option').forEach(opt => {
      opt.classList.remove('topbar-search__type-option--active');
    });
    option.classList.add('topbar-search__type-option--active');

    this.setInputPlaceholder();

    // Update label text
    this.typeLabel.textContent = option.textContent;

    // Update type
    this.currentType = type;
    this.closeTypeMenu();

    // Invalidate cache (different search type = new search)
    this.searchCache = null;

    // Only perform search if there's a query (user switching type mid-search)
    // Empty query returns same "nearest upcoming showings" regardless of type,
    // so no need to re-fetch when there's no search term
    const currentQuery = this.input.value.trim();
    if (currentQuery.length >= TopbarSearch.MIN_QUERY_LENGTH) {
      this.performSearchAllFilters(currentQuery);
    }
  }

  setInputPlaceholder() {
    const option = this.container.querySelector('.js-topbar-search-type-option.topbar-search__type-option--active');
    this.input.setAttribute('placeholder', option.dataset.placeholder);
  }

  openTypeMenu() {
    LayoutUtils.show(this.typeMenu);
    this.isTypeMenuOpen = true;
  }

  closeTypeMenu() {
    LayoutUtils.hide(this.typeMenu);
    this.isTypeMenuOpen = false;
  }

  // Perform search for ALL filters in parallel
  // Results are cached and tab counts are updated
  async performSearchAllFilters(query) {
    // Cancel any in-flight requests
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    // Clear cache for new search
    this.searchCache = {
      query: query,
      type: this.currentType,
      results: {}
    };

    this.showMessage('Searching...');
    this.openDropdown();

    // Get list of filters that have tabs (some may be hidden, e.g., follow-ups)
    const visibleFilters = this.getVisibleFilters();

    // Create fetch promises for all filters
    const fetchPromises = visibleFilters.map(filter =>
      this.fetchFilterResults(query, filter, this.abortController.signal)
    );

    try {
      // Wait for all fetches to complete
      const results = await Promise.all(fetchPromises);

      // Store results in cache
      results.forEach(({ filter, html, count }) => {
        this.searchCache.results[filter] = { html, count };
      });

      // Update tab counts
      this.updateTabCounts();

      // Display results for current filter
      this.displayCachedResults();

    } catch (err) {
      // Ignore abort errors (expected when new search starts)
      if (err.name === 'AbortError') {
        return;
      }

      FetchUtils.handleResponseError(err);
      this.showMessage('Search failed. Please try again.');
    }
  }

  // Get list of visible filter IDs from the tabs
  getVisibleFilters() {
    const tabs = this.tabsContainer.querySelectorAll('[data-filter]');
    return Array.from(tabs).map(tab => tab.dataset.filter);
  }

  // Fetch results for a single filter
  async fetchFilterResults(query, filter, signal) {
    const params = new URLSearchParams({
      q: query,
      type: this.currentType,
      filter: filter
    });

    const response = await fetch(`${this.searchUrl}?${params}`, {
      headers: {
        'X-CSRF-Token': FetchUtils.CSRFToken(),
        'Content-Type': 'application/json; charset=utf-8'
      },
      signal
    });

    FetchUtils.checkResponseStatus(response);
    const json = await response.json();

    return { filter, html: json.html, count: json.count };
  }

  // Update tab labels with counts (only when there's a search query and count > 0)
  updateTabCounts() {
    if (!this.searchCache) return;

    const hasQuery = this.searchCache.query && this.searchCache.query.length > 0;

    this.tabsContainer.querySelectorAll('[data-filter]').forEach(tab => {
      const filter = tab.dataset.filter;
      const cached = this.searchCache.results[filter];
      const count = cached?.count || '0';

      // Remove existing badge if any
      const existingBadge = tab.querySelector('.topbar-search__tab-badge');
      if (existingBadge) {
        existingBadge.remove();
      }

      // Only show badge when there's a search query AND count is not zero
      if (hasQuery && count !== '0') {
        const badge = document.createElement('span');
        badge.className = 'topbar-search__tab-badge';
        badge.textContent = count;
        tab.appendChild(badge);
      }
    });
  }

  // Display results from cache for current filter
  displayCachedResults() {
    if (!this.searchCache || !this.searchCache.results[this.currentFilter]) {
      return;
    }

    const cached = this.searchCache.results[this.currentFilter];
    this.resultsContainer.innerHTML = cached.html;

    // Initialize Lucide icons for dynamically loaded content
    LayoutUtils.refreshIcons();

    // Bind action button handlers
    this.bindActionHandlers();
  }

  showMessage(text) {
    this.resultsContainer.innerHTML = `
      <div class="topbar-search__message">
        <p>${text}</p>
      </div>
    `;
  }

  openDropdown() {
    LayoutUtils.show(this.dropdown);
    this.isOpen = true;
  }

  closeDropdown() {
    LayoutUtils.hide(this.dropdown);
    this.isOpen = false;
  }

  toggleClearButton(show) {
    // Query for search icon dynamically since lucide replaces <i> with <svg>
    const searchIcon = this.container.querySelector('.js-topbar-search-icon');
    if (show) {
      LayoutUtils.show(this.clearButton);
      LayoutUtils.hide(searchIcon);
    } else {
      LayoutUtils.hide(this.clearButton);
      LayoutUtils.show(searchIcon);
    }
  }

  // Bind click handlers for action buttons in search results
  bindActionHandlers() {
    // Result rows + edit/message buttons (shared with universal search)
    this.#resultActions.bind(this.resultsContainer);

    // "More Showings" form - populate hidden field before submit
    this.bindAllResultsForm();
  }

  // Bind form submit handler for "More Showings"
  bindAllResultsForm() {
    const form = this.resultsContainer.querySelector('.js-topbar-all-results-form');
    if (form) {
      form.addEventListener('submit', (e) => {
        const query = this.input.value.trim();

        // If no search query, just navigate to dashboard with the current filter
        // (don't trigger search results mode)
        if (!query) {
          e.preventDefault();
          const sfField = form.querySelector('input[name="sf"]');
          const filter = sfField ? sfField.value : '2';
          this.buildAndSubmitSearchForm({ filter });
          return;
        }

        // If we have a query, populate the appropriate search field
        const peopleField = form.querySelector('.js-topbar-all-results-people');
        const addressField = form.querySelector('.js-topbar-all-results-address');

        // Clear both fields first, then set the appropriate one
        if (peopleField) peopleField.value = '';
        if (addressField) addressField.value = '';

        const targetField = this.searchParamName === 'people_search' ? peopleField : addressField;
        if (targetField) targetField.value = query;
      });
    }
  }

}

// Auto-initialize on DOMContentLoaded
document.addEventListener('DOMContentLoaded', function() {
  const searchContainer = document.querySelector('.js-topbar-search');
  if (searchContainer) {
    const search = new TopbarSearch(searchContainer);
    search.init();
  }
});

export default TopbarSearch;
